import { useContext, useState } from "react";
import { useSnackbar } from "notistack";
import { useParams, useHistory } from "react-router-dom";
import { Box, Typography, Grid, CircularProgress, TextField, Paper, List, ListItem, ListItemText, Divider } from "@mui/material";

import Layout from "../components/Layout/Layout";
import { useCreateSparePartMutation, useFetchMostRecentWorkshopLine, useFetchWorkshopProductsQuery } from "../api";
import { UserDataContext } from "../app/App";
import LoadingButton from "../components/LoadingButton/LoadingButton";

function SpareParts() {
    const history = useHistory();
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");

    const { enqueueSnackbar } = useSnackbar();
    const { equipmentId, workshopOrderId, workshopLineId } = useParams();

    const { user } = useContext(UserDataContext);

    const { data: products, isLoading: isLoadingProducts, isSuccess: isSuccessProducts, isError: isErrorProducts } = useFetchWorkshopProductsQuery();

    const { data: workshopLine, isLoading: isLoadingWorkshopLine, isSuccess: isSuccessWorkshopLine, isError: isErrorWorkshopLine } = useFetchMostRecentWorkshopLine(workshopOrderId, user.id);

    const { id = null } = workshopLine || {};

    const { isLoading: isLoadingSparePart, mutate: createSparePart } = useCreateSparePartMutation({
        onSuccess: () => {
            enqueueSnackbar("Spare part registered succesfully", {
                variant: "success",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                autoHideDuration: 3000,
            });
            history.push(`/workshop/${equipmentId}/${workshopOrderId}`, { replace: true });
        },
        onError: () => {
            enqueueSnackbar("Error occurred while registering spare part", {
                variant: "error",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                autoHideDuration: 3000,
            });
        },
    });

    if (isLoadingWorkshopLine || isLoadingProducts) {
        return (
            <Layout title={`Spare Parts`} subtitle={user.name}>
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    if (isErrorWorkshopLine || isErrorProducts) {
        return <>Error</>;
    }

    if (isSuccessWorkshopLine && workshopLineId !== id) {
        return <>Error, couldn't view spare parts for this work session. Make sure you have an ongoing work session for the order.</>;
    }

    const handleListItemClick = (event, product, index) => {
        setSelectedProduct(product);
        setSelectedIndex(index);
    };

    const handleRegister = () => {
        if (!selectedProduct || (selectedProduct && !selectedProduct.id)) {
            return;
        }
        const parsedQuantity = parseFloat(quantity);

        if (isNaN(parsedQuantity)) {
            enqueueSnackbar("Quantity must be a valid number.", {
                variant: "error",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                autoHideDuration: 3000,
            });
            return;
        }
        const payload = {
            ProductId: selectedProduct.id,
            WorkshopOrderId: workshopOrderId,
            WorkshopLineId: workshopLineId,
            EmployeeId: user.id,
            Quantity: parsedQuantity,
        };

        createSparePart(payload);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    return (
        <Layout title={`Spare Parts`} subtitle={user.name}>
            <Grid container directon="column" spacing={2}>
                <Grid item container direction="column" spacing={1}>
                    <Grid item>
                        <Box component={Paper} variant="outlined" display="flex" flexDirection="column" elevation={0} p={2}>
                            <TextField label="Search product" type="search" variant="filled" fullWidth value={searchTerm} onChange={handleSearchChange} />
                        </Box>
                    </Grid>

                    <Grid item>
                        <Box component={Paper} variant="outlined" elevation={0} p={2}>
                            <Box>
                                <Typography color="textSecondary">{`Workshop Products`}</Typography>
                                <Divider style={{ backgroundColor: "darkgray" }} />
                            </Box>

                            <Box style={{ maxHeight: "300px", overflow: "auto" }}>
                                <List style={{ height: "300px" }}>
                                    {isSuccessProducts &&
                                        products &&
                                        products
                                            .filter(
                                                (product) => product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.productNumber.toLowerCase().includes(searchTerm.toLowerCase())
                                            )
                                            .map((product, index) => (
                                                <ListItem key={product.id} button selected={selectedIndex === index} onClick={(event) => handleListItemClick(event, product, index)}>
                                                    <ListItemText primary={product.name} secondary={product.productNumber + (product.description ? ` - ${product.description}` : "")} />
                                                </ListItem>
                                            ))}
                                </List>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Grid item style={{ width: "100%" }}>
                    <Box component={Paper} variant="outlined" display="flex" flexDirection="column" elevation={0} p={2} mt={1}>
                        <Box>
                            <Typography color="textSecondary">{`Selected Product`}</Typography>
                        </Box>

                        <Box my={1}>
                            <Divider style={{ backgroundColor: "darkgray" }} />
                        </Box>

                        <Box display="flex" justifyContent="space-between">
                            <Typography color="textSecondary">{`Name`}</Typography>
                            <Typography>{selectedProduct && selectedProduct?.name ? selectedProduct?.name : "-"}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Typography color="textSecondary">{`Number`}</Typography>
                            <Typography>{selectedProduct && selectedProduct?.productNumber ? selectedProduct?.productNumber : "-"}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Typography color="textSecondary">{`Description`}</Typography>
                            <Typography>{selectedProduct && selectedProduct?.description ? selectedProduct?.description : "-"}</Typography>
                        </Box>

                        <Box my={1}>
                            <Divider style={{ backgroundColor: "darkgray" }} />
                        </Box>

                        <Box>
                            <Box mb={1}>
                                <TextField
                                    label="Quantity"
                                    type="text"
                                    fullWidth
                                    disabled={!selectedProduct}
                                    value={quantity}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    variant="filled"
                                    onChange={(event) => {
                                        const regex = /^[0-9]+(\.[0-9]{0,2})?$/;
                                        const input = event.target.value;
                                        if (regex.test(input) || input === "") {
                                            // Update the quantity state only if the input is valid or empty
                                            setQuantity(input);
                                        }
                                    }}
                                />
                            </Box>

                            <LoadingButton onClick={handleRegister} disabled={!selectedProduct && quantity < 1} loading={isLoadingSparePart} variant="contained" color="primary" fullWidth>
                                {`Register`}
                            </LoadingButton>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Layout>
    );
}

export default SpareParts;
